import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, ShieldCheck, Loader2, ArrowRight, ChevronLeft } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState('phone') // 'phone' | 'otp'
  const [phone, setPhone] = useState(() => {
    const saved = localStorage.getItem('budget_last_phone') || ''
    return saved
  })
  const [autoLoggedOut] = useState(() => !!localStorage.getItem('budget_last_phone'))
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [devCode, setDevCode] = useState('')

  const handleRequestOTP = async (e) => {
    e.preventDefault()
    if (!phone.trim()) return
    setLoading(true)
    setError('')
    try {
      const data = await api.requestOTP(phone.trim())
      if (data.dev_code) setDevCode(data.dev_code)
      setStep('otp')
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const handleVerifyOTP = async (e) => {
    e.preventDefault()
    if (!code.trim()) return
    setLoading(true)
    setError('')
    try {
      const data = await api.verifyOTP(phone.trim(), code.trim())
      localStorage.removeItem('budget_last_phone')
      login(data.token, data.user)
      navigate('/')
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-600 flex flex-col items-center justify-center px-5 safe-top safe-bottom relative overflow-hidden">

      {/* Décoration fond */}
      <div className="absolute -top-24 -right-24 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-36 -left-20 w-96 h-96 bg-black/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-sm relative z-10">

        {/* Header / Branding */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white/15 backdrop-blur-sm border border-white/25 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
            <span className="text-4xl select-none">💰</span>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Budget Familial</h1>
          <p className="text-primary-200 text-sm mt-1.5">Gérez votre budget ensemble</p>
        </div>

        {/* Indicateur d'étape */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === 'phone' ? 'bg-white text-primary-700 shadow-lg' : 'bg-green-400 text-white'}`}>
              {step !== 'phone' ? '✓' : '1'}
            </div>
            <span className={`text-xs font-medium transition-colors ${step === 'phone' ? 'text-white' : 'text-primary-300'}`}>Numéro</span>
          </div>
          <div className={`h-px w-10 transition-all ${step === 'otp' ? 'bg-white/70' : 'bg-white/20'}`} />
          <div className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === 'otp' ? 'bg-white text-primary-700 shadow-lg' : 'bg-white/20 text-white/40'}`}>2</div>
            <span className={`text-xs font-medium transition-colors ${step === 'otp' ? 'text-white' : 'text-white/40'}`}>Code OTP</span>
          </div>
        </div>

        {/* Carte principale */}
        <div className="bg-white rounded-3xl p-7 shadow-2xl">

          {step === 'phone' ? (
            <form onSubmit={handleRequestOTP} className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Connexion</h2>
                <p className="text-sm text-slate-400 mt-0.5">Entrez votre numéro pour recevoir un code de vérification</p>
              </div>

              {autoLoggedOut && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <span className="text-amber-500 shrink-0">⏱</span>
                  <p className="text-xs text-amber-700">Session expirée après 30 min d&apos;inactivité</p>
                </div>
              )}

              <div>
                <label className="label">Numéro de téléphone</label>
                <div className="relative">
                  <Phone size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+242 06 000 00 00"
                    className="input pl-10"
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !phone.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <><span>Recevoir le code</span><ArrowRight size={16} /></>}
              </button>
            </form>

          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setCode(''); setError('') }}
                  className="p-1.5 -ml-1 rounded-lg text-slate-400 active:bg-slate-100 mt-0.5 shrink-0"
                >
                  <ChevronLeft size={20} />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Vérification</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Code envoyé au <span className="font-semibold text-slate-600">{phone}</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="label">Code à 6 chiffres</label>
                <div className="relative">
                  <ShieldCheck size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="· · · · · ·"
                    className="input pl-10 text-center text-2xl tracking-[0.6em] font-bold"
                    autoFocus
                  />
                </div>
              </div>

              {devCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
                  <span className="text-xl shrink-0">🔧</span>
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Mode développement</p>
                    <p className="text-lg font-bold text-amber-700 tracking-widest">{devCode}</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <><span>Se connecter</span><ArrowRight size={16} /></>}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-primary-300/80 text-xs mt-6 px-6 leading-relaxed">
          Accès réservé aux membres enregistrés par l&apos;administrateur.
        </p>
      </div>
    </div>
  )
}
